import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { authService, LoginRequest, RegisterRequest } from "../services/auth";
import { useNavigate } from "react-router-dom";

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; message?: string }>;
  register: (
    name: string,
    email: string,
    password: string,
    phoneNumber: string,
    roleID: string,
    organizationID: string,
    know_from: string,
    court_id: string
  ) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>; 
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // <-- new

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (storedToken && storedUser && storedRefreshToken) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false); // <-- finished restoring
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const credentials: LoginRequest = { email, password };
      const response = await authService.login(credentials);
      console.log(response);
      
      if (response.success == "0") {
        return { success: false, message: response.message };
      }

      setToken(response.token);
      setUser(response.user);
      localStorage.setItem("token", response.token);
      localStorage.setItem("refreshToken", response.refreshToken);
      localStorage.setItem("user", JSON.stringify(response.user));

      return { success: true };
    } catch (error: any) {
      return { success: false, message: error.message };
    }
  };

  const register = async (
    name: string,
    email: string,
    password: string,
    phone: string,
    roleID: "0",
    organizationID: "0",
    know_from: string,
    court_id: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const userData: RegisterRequest = {
        name,
        email,
        password,
        phone,
        roleID,
        organizationID,
        know_from,
        court_id
      };
      const response = await authService.register(userData);

      // If the API indicates failure, stop early
      if (response?.success == "0") {
        return { success: false, message: response.message };
      }

      // Optionally persist tokens if registration already returns them
      if (response?.token && response?.refreshToken && response?.user) {
        setToken(response.token);
        setUser(response.user);
        localStorage.setItem("token", response.token);
        localStorage.setItem("refreshToken", response.refreshToken);
        localStorage.setItem("user", JSON.stringify(response.user));
      }

      // Auto-login using the provided credentials to ensure session consistency
      const loginResult = await login(email, password);
      return {
        success: loginResult.success === true,
        message: response.message,
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const logout = async () => {
    try {
      // await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    }
    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!token && !!user,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
